import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getCompanyList} from "../../../../api/company/CompanyService";
import {getUser, getUserAssetCount} from "../../../../api/user/UserAccountService";
import UserAccountDetail from "./UserAccountDetail";
import UserAccountAsset from "./UserAccountAsset";
import UserAccountEditForm from "./UserAccountEditForm";
import PanelTextNav from "../../fieldMng/shared/PanelTextNav";
import Annonymous from "../../../../resources/icon/annonymous.jpg";
import {login} from "../../../../redux/slice/authSlice";
import {canConnectAsUser, connectAsUserAccount} from "../userAccountConnect";

const USER_TAB_OPTIONS = [
    {id: "info", label: "기본정보"},
    {id: "operator", label: "운영 자산"},
    {id: "handler", label: "사용 자산"},
];

function authorityString(userAccount) {
    if (!userAccount) {
        return "";
    }
    if (userAccount.serviceReceiveAuthority) {
        return "서비스접수권한 있음";
    }
    return "권한 없음";
}

function UserAccountManageDetailPanel({userId, onEditSuccess}) {
    const IMG_BASE = import.meta.env.VITE_IMG_BASE;
    const viewer = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState("info");
    const [userAccount, setUserAccount] = useState(null);
    const [company, setCompany] = useState(null);
    const [count, setCount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const selectedIdRef = React.useRef(null);
    const editFormRef = useRef(null);

    const loadDetail = useCallback(async (id) => {
        if (id == null) {
            setUserAccount(null);
            setCompany(null);
            setCount(null);
            selectedIdRef.current = null;
            return;
        }

        selectedIdRef.current = id;
        setLoading(true);
        setActiveTab("info");
        setIsEditing(false);
        try {
            const response = await getUser(id);
            if (selectedIdRef.current !== id) {
                return;
            }
            if (response?.status !== 200) {
                return;
            }

            const user = response.data;
            setUserAccount(user);

            const [countResponse, companyResponse] = await Promise.all([
                getUserAssetCount(user.id),
                user.company?.id
                    ? getCompanyList({searchTxt: "", entityId: user.company.id}, {
                        currentPage: 0,
                        size: 10,
                        sort: "createdAt",
                        desc: true,
                    })
                    : Promise.resolve(null),
            ]);

            if (selectedIdRef.current !== id) {
                return;
            }

            setCount(countResponse?.data ?? null);
            setCompany(companyResponse?.data?.content?.[0] ?? null);
        } finally {
            if (selectedIdRef.current === id) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        void loadDetail(userId);
    }, [userId, loadDetail]);

    if (loading) {
        return (
            <div className="code-editor-empty">
                <div className="code-editor-empty__icon">…</div>
                <p className="code-editor-empty__title">불러오는 중</p>
                <p className="code-editor-empty__desc">사용자 정보를 가져오고 있습니다.</p>
            </div>
        );
    }

    if (!userAccount) {
        return null;
    }

    const showConnectButton = canConnectAsUser(viewer, userAccount);

    const handleConnect = async () => {
        const success = await connectAsUserAccount(userAccount.id);
        if (!success) {
            return;
        }
        dispatch(login(viewer));
        window.location.reload();
    };

    const handleStartEdit = () => {
        setActiveTab("info");
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        void loadDetail(userAccount.id);
    };

    const handleSaveEdit = () => {
        editFormRef.current?.submit();
    };

    const handleEditSuccess = () => {
        setIsEditing(false);
        void loadDetail(userAccount.id);
        onEditSuccess?.();
    };

    const handleTabChange = (tab) => {
        if (isEditing) {
            setIsEditing(false);
        }
        setActiveTab(tab);
    };

    const bodyClassName = [
        "user-account-manage-detail__body",
        activeTab !== "info" ? "user-account-manage-detail__body--asset" : "",
        isEditing && activeTab === "info" ? "user-account-manage-detail__body--edit" : "",
    ].filter(Boolean).join(" ");

    return (
        <div className="user-account-manage-detail">
            <div className="user-account-manage-detail__profile">
                <img
                    alt=""
                    className="user-account-manage-detail__avatar"
                    src={userAccount.profileImage ? `${IMG_BASE}${userAccount.profileImage}` : Annonymous}
                />
                <div className="user-account-manage-detail__profile-text">
                    <p className="user-account-manage-detail__name">{userAccount.name}</p>
                    <p className="user-account-manage-detail__auth">{authorityString(userAccount)}</p>
                </div>
                <div className="user-account-manage-detail__profile-actions">
                    {userAccount.auth?.update !== false && (
                        isEditing ? (
                            <>
                                <button
                                    type="button"
                                    className="user-account-manage-detail__connect-btn"
                                    onClick={handleCancelEdit}
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    className="user-account-manage-detail__edit-btn"
                                    onClick={handleSaveEdit}
                                >
                                    저장
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="user-account-manage-detail__edit-btn"
                                onClick={handleStartEdit}
                            >
                                수정
                            </button>
                        )
                    )}
                    {showConnectButton && !isEditing && (
                        <button
                            type="button"
                            className="user-account-manage-detail__connect-btn"
                            onClick={() => void handleConnect()}
                        >
                            접속
                        </button>
                    )}
                </div>
            </div>

            <div className="user-account-manage-detail__tabs">
                <PanelTextNav
                    ariaLabel="사용자 상세 탭"
                    options={USER_TAB_OPTIONS.map((option) => ({
                        ...option,
                        label: option.id === "operator" && count?.operator != null
                            ? `${option.label} (${count.operator})`
                            : option.id === "handler" && count?.handler != null
                                ? `${option.label} (${count.handler})`
                                : option.label,
                    }))}
                    value={activeTab}
                    onChange={handleTabChange}
                />
            </div>

            <div className={bodyClassName}>
                {activeTab === "info" && isEditing && (
                    <UserAccountEditForm
                        ref={editFormRef}
                        key={`edit-${userAccount.id}`}
                        userId={userAccount.id}
                        initialData={userAccount}
                        embedded
                        hideActions
                        onCancel={handleCancelEdit}
                        onSuccess={handleEditSuccess}
                    />
                )}
                {activeTab === "info" && !isEditing && (
                    <UserAccountDetail userAccount={userAccount} company={company} />
                )}
                {(activeTab === "operator" || activeTab === "handler") && (
                    <UserAccountAsset show={activeTab} userAccount={userAccount} embedded />
                )}
            </div>
        </div>
    );
}

export default UserAccountManageDetailPanel;
